/**
 * ProgressDisplay Component
 * 
 * Unified design system component for displaying progress percentages across the application.
 * Supports multiple display variants while maintaining consistent calculation and formatting.
 * 
 * @example
 * // Simple text display (for tables)
 * <ProgressDisplay percentage={45.5} variant="text" />
 * 
 * // Chip display (for cards)
 * <ProgressDisplay percentage={75.0} variant="chip" status="on-track" />
 * 
 * // Progress bar (for detailed views)
 * <ProgressDisplay percentage={60.0} variant="bar" showPercentage />
 * 
 * // Full display with icon and label
 * <ProgressDisplay percentage={85.0} variant="full" label="Overall Progress" status="on-track" />
 */

import React from 'react';
import {
  Box,
  Typography,
  LinearProgress,
  Chip,
  Tooltip,
  useTheme
} from '@mui/material';
import {
  CheckCircle,
  Schedule,
  Warning,
  Error as ErrorIcon
} from '@mui/icons-material';
import { getStatusHexColor, getIndicatorColor } from '../../utils/statusColors';

// ============================================================================
// TYPES AND INTERFACES
// ============================================================================

/**
 * Progress status levels for color coding and icons
 */
export type ProgressStatus = 'on-track' | 'at-risk' | 'overdue' | 'not-started' | 'completed';

/**
 * Display variants for different use cases
 */
export type ProgressVariant = 
  | 'text'      // Simple text percentage (for tables)
  | 'chip'      // Colored chip with percentage (for cards)
  | 'bar'       // Progress bar with optional percentage (for detailed views)
  | 'full';     // Complete display with icon, label, and bar (for comprehensive views)

/**
 * Size options for the component
 */
export type ProgressSize = 'small' | 'medium' | 'large';

/**
 * Props for the ProgressDisplay component
 */
export interface ProgressDisplayProps {
  /** Progress percentage (0-100) */
  percentage: number;
  
  /** Display variant */
  variant?: ProgressVariant;
  
  /** Progress status for color coding (optional) */
  status?: ProgressStatus;
  
  /** Label text (used in 'full' variant) */
  label?: string;
  
  /** Whether to show icon (used in 'full' variant) */
  showIcon?: boolean;
  
  /** Whether to show percentage text (used in 'bar' variant) */
  showPercentage?: boolean;
  
  /** Component size */
  size?: ProgressSize;
  
  /** Number of decimal places (default: 1) */
  precision?: number;
  
  /** Tooltip text (optional) */
  tooltip?: string;
  
  /** Whether to show "-" for zero/undefined values instead of "0.0%" */
  showDashForEmpty?: boolean;
  
  /** Custom color override (hex color) */
  customColor?: string;
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get status color based on progress status
 */
const getStatusColor = (status: ProgressStatus | undefined, isDarkMode: boolean): string => {
  if (!status) return getStatusHexColor('in progress', isDarkMode);
  
  switch (status) {
    case 'completed':
      return getStatusHexColor('done', isDarkMode);
    case 'on-track':
      return getStatusHexColor('in progress', isDarkMode);
    case 'at-risk':
      return getIndicatorColor('at-risk', isDarkMode);
    case 'overdue':
      return getIndicatorColor('overdue', isDarkMode);
    case 'not-started':
      return getStatusHexColor('backlog', isDarkMode);
    default:
      return getStatusHexColor('in progress', isDarkMode);
  }
};

/**
 * Get status icon based on progress status
 */
const getStatusIcon = (status: ProgressStatus | undefined) => {
  if (!status) return <Schedule color="primary" fontSize="small" />;
  
  switch (status) {
    case 'completed':
      return <CheckCircle color="success" fontSize="small" />;
    case 'on-track':
      return <Schedule color="primary" fontSize="small" />;
    case 'at-risk':
      return <Warning color="warning" fontSize="small" />;
    case 'overdue':
      return <ErrorIcon color="error" fontSize="small" />;
    case 'not-started':
      return <Schedule color="disabled" fontSize="small" />;
    default:
      return <Schedule color="primary" fontSize="small" />;
  }
};

/**
 * Get MUI color name for LinearProgress
 */
const getProgressColor = (status: ProgressStatus | undefined): 'primary' | 'success' | 'warning' | 'error' => {
  if (!status) return 'primary';
  
  switch (status) {
    case 'completed':
      return 'success';
    case 'on-track':
      return 'primary';
    case 'at-risk':
      return 'warning';
    case 'overdue':
      return 'error';
    default:
      return 'primary';
  }
};

/**
 * Format percentage with specified precision
 */
const formatPercentage = (percentage: number, precision: number): string => {
  return `${percentage.toFixed(precision)}%`;
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

/**
 * Unified progress display component
 */
export const ProgressDisplay: React.FC<ProgressDisplayProps> = ({
  percentage,
  variant = 'text',
  status,
  label,
  showIcon = true,
  showPercentage = true,
  size = 'medium',
  precision = 1,
  tooltip,
  showDashForEmpty = false,
  customColor
}) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  
  // Determine if we should show empty state
  const isEmpty = percentage === 0 || percentage === undefined || isNaN(percentage);
  const shouldShowDash = showDashForEmpty && isEmpty;
  
  // Get colors
  const statusColor = customColor || getStatusColor(status, isDarkMode);
  const progressColorName = getProgressColor(status);
  
  // Format the percentage text
  const percentageText = formatPercentage(percentage, precision);
  
  // Get size-specific values
  const fontSize = size === 'small' ? '0.75rem' : size === 'large' ? '1rem' : '0.85rem';
  const barHeight = size === 'small' ? 6 : size === 'large' ? 8 : 6;
  
  // ============================================================================
  // VARIANT: TEXT (Simple text for tables)
  // ============================================================================
  
  if (variant === 'text') {
    const content = (
      <Typography 
        variant="body2" 
        sx={{ 
          fontSize,
          fontWeight: 500,
          color: shouldShowDash ? 'text.secondary' : 'text.primary'
        }}
      >
        {shouldShowDash ? '-' : percentageText}
      </Typography>
    );
    
    if (tooltip) {
      return <Tooltip title={tooltip}>{content}</Tooltip>;
    }
    
    return content;
  }
  
  // ============================================================================
  // VARIANT: CHIP (Colored chip for cards)
  // ============================================================================
  
  if (variant === 'chip') {
    const chipContent = (
      <Chip
        label={shouldShowDash ? '-' : percentageText}
        size={size === 'large' ? 'medium' : 'small'}
        sx={{
          fontSize,
          backgroundColor: shouldShowDash ? 'action.disabledBackground' : statusColor,
          color: shouldShowDash ? 'text.disabled' : 'white',
          fontWeight: 500,
          border: 'none',
          '&:hover': {
            backgroundColor: shouldShowDash ? 'action.disabledBackground' : statusColor
          }
        }}
      />
    );
    
    if (tooltip) {
      return <Tooltip title={tooltip}>{chipContent}</Tooltip>;
    }
    
    return chipContent;
  }
  
  // ============================================================================
  // VARIANT: BAR (Progress bar with optional percentage)
  // ============================================================================
  
  if (variant === 'bar') {
    const barColor = customColor || getStatusColor(status, isDarkMode);
    const clampedPercentage = Math.min(Math.max(percentage, 0), 100);

    return (
      <div style={{
        width: '100%',
        height: `${barHeight}px`,
        lineHeight: 0,
        fontSize: 0,
        display: 'block'
      }}>
        <Tooltip title={tooltip || `${percentageText} complete`}>
          <div
            style={{
              width: '100%',
              height: `${barHeight}px`,
              minHeight: `${barHeight}px`,
              maxHeight: `${barHeight}px`,
              backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
              borderRadius: '4px',
              overflow: 'hidden',
              position: 'relative',
              display: 'block',
              lineHeight: 0,
              fontSize: 0,
              boxSizing: 'border-box'
            }}
          >
          <div
            style={{
              width: `${clampedPercentage}%`,
              height: `${barHeight}px`,
              minHeight: `${barHeight}px`,
              maxHeight: `${barHeight}px`,
              backgroundColor: barColor,
              borderRadius: '4px',
              transition: 'width 0.3s ease-in-out',
              position: 'absolute',
              top: 0,
              left: 0,
              lineHeight: 0,
              fontSize: 0,
              boxSizing: 'border-box'
            }}
          />
        </div>
        </Tooltip>

        {showPercentage && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
            <Typography variant="caption" color="text.secondary">
              {shouldShowDash ? 'No data' : `${percentageText} complete`}
            </Typography>
            {status && (
              <Typography variant="caption" color="text.secondary">
                {status.replace('-', ' ')}
              </Typography>
            )}
          </Box>
        )}
      </div>
    );
  }
  
  // ============================================================================
  // VARIANT: FULL (Complete display with icon, label, and bar)
  // ============================================================================
  
  if (variant === 'full') {
    return (
      <Box sx={{ width: '100%' }}>
        {label && (
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}>
            {showIcon && getStatusIcon(status)}
            <Typography 
              variant={size === 'small' ? 'caption' : 'body2'} 
              sx={{ fontWeight: 500 }}
            >
              {label}
            </Typography>
            <Chip
              label={shouldShowDash ? '-' : percentageText}
              size="small"
              sx={{
                ml: 'auto',
                fontSize: '0.75rem',
                backgroundColor: shouldShowDash ? 'action.disabledBackground' : statusColor,
                color: shouldShowDash ? 'text.disabled' : 'white',
                fontWeight: 500,
                border: 'none',
                '&:hover': {
                  backgroundColor: shouldShowDash ? 'action.disabledBackground' : statusColor
                }
              }}
            />
          </Box>
        )}
        
        <Tooltip title={tooltip || `${percentageText} complete - ${status?.replace('-', ' ') || 'in progress'}`}>
          <Box
            sx={{
              width: '100%',
              height: `${barHeight}px`,
              minHeight: `${barHeight}px`,
              maxHeight: `${barHeight}px`,
              backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
              borderRadius: '4px',
              overflow: 'hidden',
              position: 'relative',
              display: 'block',
              lineHeight: 0,
              fontSize: 0
            }}
          >
            <Box
              sx={{
                width: `${Math.min(Math.max(percentage, 0), 100)}%`,
                height: `${barHeight}px`,
                minHeight: `${barHeight}px`,
                maxHeight: `${barHeight}px`,
                backgroundColor: customColor || getStatusColor(status, isDarkMode),
                borderRadius: '4px',
                transition: 'width 0.3s ease-in-out',
                position: 'absolute',
                top: 0,
                left: 0,
                lineHeight: 0,
                fontSize: 0
              }}
            />
          </Box>
        </Tooltip>
        
        {!label && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
            <Typography variant="caption" color="text.secondary">
              {shouldShowDash ? 'No data' : `${percentageText} complete`}
            </Typography>
            {status && (
              <Typography variant="caption" color="text.secondary">
                {status.replace('-', ' ')}
              </Typography>
            )}
          </Box>
        )}
      </Box>
    );
  }
  
  // Fallback to text variant
  return (
    <Typography variant="body2" sx={{ fontSize }}>
      {shouldShowDash ? '-' : percentageText}
    </Typography>
  );
};

export default ProgressDisplay;

